import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { toast } from 'sonner';
import { TrendUp, Eye, EyeSlash } from '@phosphor-icons/react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { extractApiError } from '@/lib/apiErrors';
import Logo from '@/components/Logo';

// Stronger client-side check mirroring backend rejection list — gives users
// immediate feedback instead of a round-trip 422.
const COMMON_PASSWORDS = new Set(['password', '12345678', '123456', '123456789', 'qwerty', 'qwerty123', 'abc123']);

const RegisterPage = () => {
  const navigate = useNavigate();
  const { register, isAuthenticated } = useAuth();
  const { t, language } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [justSubmitted, setJustSubmitted] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '', password: '', first_name: '', last_name: '', organization_name: ''
  });

  useEffect(() => {
    // Only redirect users who arrived already authenticated; don't override the
    // post-submit navigate(/dashboard?onboarding=1) for brand-new registrations.
    if (isAuthenticated && !justSubmitted) navigate('/dashboard');
  }, [isAuthenticated, justSubmitted, navigate]);

  if (isAuthenticated && !justSubmitted) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Explicit validation with toast feedback (more visible than browser native tooltip)
    if (!formData.email?.trim()) { toast.error(t('register.error.email_required')); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) { toast.error(t('register.error.email_invalid')); return; }
    if (!formData.password || formData.password.length < 6) { toast.error(t('register.error.password_short')); return; }
    if (COMMON_PASSWORDS.has(formData.password.toLowerCase())) {
      toast.error(language === 'tr' ? 'Şifreniz çok yaygın — daha güçlü bir şifre seçin' : 'Password is too common — choose a stronger one');
      return;
    }
    if (!formData.first_name?.trim() || !formData.last_name?.trim()) { toast.error(t('register.error.name_required')); return; }
    if (!formData.organization_name?.trim()) { toast.error(t('register.error.org_required')); return; }

    setLoading(true);
    try {
      await register(formData);
      setJustSubmitted(true);
      toast.success(t('register.success'));
      navigate('/dashboard?onboarding=1');
    } catch (error) {
      toast.error(extractApiError(error, t('register.error'), language));
    } finally {
      setLoading(false);
    }
  };

  const benefitItems = [
    t('register.benefit.1'), t('register.benefit.2'),
    t('register.benefit.3'), t('register.benefit.4')
  ];

  return (
    <div className="min-h-screen bg-white flex">
      <div className="hidden lg:flex flex-1 bg-[#F4F4F5] items-center justify-center p-12">
        <div className="max-w-md">
          <h2 className="heading-2 text-[#09090B] mb-6">{t('register.panel.title')}</h2>
          <ul className="space-y-4">
            {benefitItems.map((item, index) => (
              <li key={index} className="flex items-start gap-3">
                <div className="w-6 h-6 bg-[#0F1B36] flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-white text-sm font-bold">{index + 1}</span>
                </div>
                <span className="text-[#09090B]">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="flex items-center justify-between mb-12">
            <Link to="/" className="flex items-center gap-2">
              <Logo variant="light" size="md" />
            </Link>
            <LanguageSwitcher />
          </div>

          <h1 className="heading-2 text-[#09090B] mb-2" data-testid="register-title">
            {t('register.title')}
          </h1>
          <p className="text-[#52525B] mb-8">{t('register.subtitle')}</p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name" className="text-[#09090B]">{t('register.first_name')}</Label>
                <Input id="first_name" type="text" placeholder="John"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  className="h-12 rounded-none border-[#E4E4E7] focus:ring-[#0F1B36]"
                  required data-testid="register-firstname-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name" className="text-[#09090B]">{t('register.last_name')}</Label>
                <Input id="last_name" type="text" placeholder="Doe"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  className="h-12 rounded-none border-[#E4E4E7] focus:ring-[#0F1B36]"
                  required data-testid="register-lastname-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#09090B]">{t('register.email')}</Label>
              <Input id="email" type="email" placeholder={t('login.email.placeholder')}
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="h-12 rounded-none border-[#E4E4E7] focus:ring-[#0F1B36]"
                required data-testid="register-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="organization" className="text-[#09090B]">{t('register.organization')}</Label>
              <Input id="organization" type="text" placeholder={t('register.organization.placeholder')}
                value={formData.organization_name}
                onChange={(e) => setFormData({ ...formData, organization_name: e.target.value })}
                className="h-12 rounded-none border-[#E4E4E7] focus:ring-[#0F1B36]"
                data-testid="register-org-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-[#09090B]">{t('register.password')}</Label>
              <div className="relative">
                <Input id="password" type={showPassword ? 'text' : 'password'} placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="h-12 rounded-none border-[#E4E4E7] focus:ring-[#0F1B36] pr-12"
                  required minLength={6} data-testid="register-password-input"
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#52525B] hover:text-[#09090B]"
                  data-testid="register-toggle-password"
                >
                  {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>
            <Button type="submit" disabled={loading}
              className="w-full h-12 rounded-none bg-[#0F1B36] hover:bg-[#00227A] text-white font-semibold"
              data-testid="register-submit-btn"
            >
              {loading ? t('register.submitting') : t('register.submit')}
            </Button>
          </form>

          <p className="mt-8 text-center text-[#52525B]">
            {t('register.has_account')}{' '}
            <Link to="/login" className="text-[#0F1B36] hover:text-[#00227A] font-medium" data-testid="register-login-link">
              {t('register.sign_in')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
